import React, { useState } from 'react';
import {
  Input,
  Textarea,
  Checkbox,
  RadioGroup,
  Switch,
  Select,
  Stack,
} from '@/components/ui';
import { CodeBlock } from '../CodeBlock';
import { SectionWrapper } from '../SectionWrapper';

export function FormElementsSection() {
  const [inputValue, setInputValue] = useState('');
  const [textareaValue, setTextareaValue] = useState('');
  const [checkboxValue, setCheckboxValue] = useState(false);
  const [radioValue, setRadioValue] = useState('option1');
  const [switchValue, setSwitchValue] = useState(false);
  const [selectValue, setSelectValue] = useState('');

  return (
    <SectionWrapper title="Form Elements">
      <div className="grid gap-8 md:grid-cols-2">
        {/* Text Inputs */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Text Inputs</h3>
          <Stack spacing={4}>
            <Input
              label="Name"
              placeholder="Enter your name"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
            />
            <Input
              label="Email"
              type="email"
              placeholder="john@example.com"
              hint="We'll never share your email"
            />
            <Input
              label="Password"
              type="password"
              placeholder="••••••••"
              error="Password must be at least 8 characters"
            />
            <Input
              label="With Icon"
              icon="👤"
              placeholder="Username"
            />
          </Stack>
          <CodeBlock>
{`<Input
  label="Name"
  placeholder="Enter your name"
  value={value}
  onChange={(e) => setValue(e.target.value)}
/>
<Input
  label="Email"
  type="email"
  placeholder="john@example.com"
  hint="We'll never share your email"
/>
<Input
  label="Password"
  type="password"
  error="Password must be at least 8 characters"
/>`}
          </CodeBlock>
        </div>

        {/* Textarea */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Textarea</h3>
          <Stack spacing={4}>
            <Textarea
              label="Message"
              placeholder="Enter your message..."
              value={textareaValue}
              onChange={(e) => setTextareaValue(e.target.value)}
            />
            <Textarea
              label="Description"
              placeholder="Describe your project..."
              hint="Maximum 500 characters"
              rows={4}
            />
          </Stack>
          <CodeBlock>
{`<Textarea
  label="Message"
  placeholder="Enter your message..."
  value={value}
  onChange={(e) => setValue(e.target.value)}
/>
<Textarea
  label="Description"
  placeholder="Describe your project..."
  hint="Maximum 500 characters"
  rows={4}
/>`}
          </CodeBlock>
        </div>

        {/* Select */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Select</h3>
          <Stack spacing={4}>
            <Select
              label="Country"
              value={selectValue}
              onChange={(e) => setSelectValue(e.target.value)}
              placeholder="Select a country"
            >
              <option value="">Select a country</option>
              <option value="us">United States</option>
              <option value="uk">United Kingdom</option>
              <option value="ca">Canada</option>
              <option value="au">Australia</option>
            </Select>
            <Select
              label="Language"
              hint="Choose your preferred language"
              defaultValue=""
            >
              <option value="">Select language</option>
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
            </Select>
          </Stack>
          <CodeBlock>
{`<Select
  label="Country"
  value={value}
  onChange={(e) => setValue(e.target.value)}
  placeholder="Select a country"
>
  <option value="">Select a country</option>
  <option value="us">United States</option>
  <option value="uk">United Kingdom</option>
  <option value="ca">Canada</option>
</Select>`}
          </CodeBlock>
        </div>

        {/* Checkboxes & Radio */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Checkboxes & Radio Buttons</h3>
          <Stack spacing={4}>
            <Checkbox
              checked={checkboxValue}
              onChange={(e) => setCheckboxValue(e.target.checked)}
              label="I agree to the terms and conditions"
            />
            <Checkbox
              label="Subscribe to newsletter"
              hint="Get weekly updates"
            />
            <RadioGroup
              value={radioValue}
              onChange={setRadioValue}
              label="Choose your plan"
              options={[
                { value: 'free', label: 'Free Plan' },
                { value: 'pro', label: 'Pro Plan ($9/month)' },
                { value: 'enterprise', label: 'Enterprise', disabled: true },
              ]}
            />
          </Stack>
          <CodeBlock>
{`<Checkbox
  checked={checked}
  onChange={(e) => setChecked(e.target.checked)}
  label="I agree to the terms"
/>

<RadioGroup
  value={value}
  onChange={setValue}
  label="Choose your plan"
  options={[
    { value: 'free', label: 'Free Plan' },
    { value: 'pro', label: 'Pro Plan' },
    { value: 'enterprise', label: 'Enterprise' },
  ]}
/>`}
          </CodeBlock>
        </div>

        {/* Switch */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Switch</h3>
          <Stack spacing={4}>
            <Switch
              checked={switchValue}
              onChange={(e) => setSwitchValue(e.target.checked)}
              label="Enable notifications"
            />
            <Switch
              label="Dark mode"
              labelPosition="left"
            />
            <Switch
              label="Maintenance mode"
              disabled
            />
          </Stack>
          <CodeBlock>
{`<Switch
  checked={checked}
  onChange={(e) => setChecked(e.target.checked)}
  label="Enable notifications"
/>
<Switch
  label="Dark mode"
  labelPosition="left"
/>`}
          </CodeBlock>
        </div>
      </div>
    </SectionWrapper>
  );
}
